/**
 * Modal dialog for setting a Promote action on a player in scenario mode.
 *
 * Shows Annual Salary (MoneyInput) and Contract Length (months TextInput).
 * Supports create mode (Cancel + Confirm) and edit mode
 * (Cancel Promote + Close + Update).
 * @module features/squad-planning/components/PromoteActionModal
 */

import React, { memo } from "react";

import LiveImpactPanel from "@/features/financial-compliance/components/molecules/LiveImpactPanel";
import MoneyInput from "@/components/molecules/MoneyInput";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Heading } from "@/components/atoms/Heading";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Text } from "@/components/atoms/Text";
import { TextInput } from "@/components/atoms/TextInput";

import type { LiveImpactModel } from "@/features/financial-compliance/types";

/** Props for {@link PromoteActionModalView} */
export interface PromoteActionModalViewProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Player name for the title */
  readonly playerName: string;
  /** Current annual salary value */
  readonly annualSalary: number | null;
  /** Current duration text (months) */
  readonly durationText: string;
  /** Called when annual salary changes */
  readonly onAnnualSalaryChange: (value: number | null) => void;
  /** Called when duration text changes */
  readonly onDurationChange: (text: string) => void;
  /** Called to confirm the promote action */
  readonly onConfirm: () => void;
  /** Called to cancel/close the modal */
  readonly onCancel: () => void;
  /** Called to remove the action (edit mode only) */
  readonly onCancelAction?: () => void;
  /** Whether the modal is in edit mode */
  readonly isEditMode?: boolean;
  /** Live Impact verdict for the scenario after this action (SE-7002) */
  readonly liveImpact: LiveImpactModel;
  /** Opens the full impact view for the current scenario */
  readonly onViewFullImpact: () => void;
}

/**
 * Renders the promote action modal with salary and contract length inputs.
 * @param props - Component props
 * @param props.isOpen - Whether modal is visible
 * @param props.playerName - Player name for title
 * @param props.annualSalary - Current annual salary
 * @param props.durationText - Duration in months as text
 * @param props.onAnnualSalaryChange - Annual salary change handler
 * @param props.onDurationChange - Duration change handler
 * @param props.onConfirm - Confirm handler
 * @param props.onCancel - Cancel/close handler
 * @param props.onCancelAction - Remove action handler (edit mode)
 * @param props.isEditMode - Whether in edit mode
 * @param props.liveImpact - Live Impact verdict for the whole scenario
 * @param props.onViewFullImpact - Opens the full impact view
 */
const PromoteActionModalView = ({
  isOpen,
  playerName,
  annualSalary,
  durationText,
  onAnnualSalaryChange,
  onDurationChange,
  onConfirm,
  onCancel,
  onCancelAction,
  isEditMode = false,
  liveImpact,
  onViewFullImpact,
}: PromoteActionModalViewProps) => (
  <Modal isOpen={isOpen} onClose={onCancel} size="md" avoidKeyboard>
    <ModalBackdrop />
    <ModalContent className="p-6">
      <ModalHeader>
        <Heading variant="title-lg">
          Promote{" "}
          <Text variant="title" className="text-accent-primary">
            {playerName}
          </Text>
        </Heading>
      </ModalHeader>
      <ModalBody>
        <Stack className="gap-4">
          <Stack className="gap-1">
            <Text variant="body-strong" className="text-content-muted">
              Annual Salary
            </Text>
            <MoneyInput
              value={annualSalary}
              onConfirm={onAnnualSalaryChange}
              onChange={onAnnualSalaryChange}
              onCancel={onCancel}
              variant="expanded"
            />
          </Stack>
          <Stack className="gap-1">
            <Text variant="body-strong" className="text-content-muted">
              Contract Length (months)
            </Text>
            <TextInput
              testID="promote-duration-input"
              className="web:outline-none web:focus:border-accent-primary h-9 w-full rounded-sm border border-outline-default px-3 py-2 text-body text-content-primary"
              value={durationText}
              onChangeText={onDurationChange}
              placeholder="12"
              placeholderTextColor="#a3a3a3"
              keyboardType="number-pad"
            />
          </Stack>
          <LiveImpactPanel
            testID="promote-live-impact"
            model={liveImpact}
            onViewFullImpact={onViewFullImpact}
          />
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Stack
          direction="horizontal"
          className="flex-1 flex-wrap items-center justify-between gap-2"
        >
          {isEditMode && onCancelAction != null ? (
            <Button
              variant="link"
              onPress={onCancelAction}
              size="md"
              label="Cancel Promote"
              labelTone="danger"
            />
          ) : (
            <Stack direction="horizontal" />
          )}
          <Stack direction="horizontal" className="gap-4">
            <Button
              variant="outline"
              onPress={onCancel}
              size="md"
              className="border-outline-strong"
              label={isEditMode ? "Close" : "Cancel"}
              labelTone="primary"
            />
            <Button
              onPress={onConfirm}
              size="md"
              className="bg-accent-primary"
              isDisabled={
                annualSalary == null ||
                annualSalary <= 0 ||
                !durationText.trim() ||
                parseInt(durationText, 10) <= 0
              }
              label={isEditMode ? "Update" : "Confirm"}
              labelTone="on-accent"
            />
          </Stack>
        </Stack>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

PromoteActionModalView.displayName = "PromoteActionModalView";

export default memo(PromoteActionModalView);
